import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useSetRecoilState } from 'recoil';
import { useUpdateEffect, useWindowSize } from 'usehooks-ts';
import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';

import { alertsSelectorAdd } from '../../../../recoil';
import { LARGE_PAGE_SIZE, LAYOUT_WIDTH_BREAKPOINTS } from '../../../../constants';
import { getPageFromSearchParams, PAGE_INDEX_PARAM, useQuery } from '../../../../utils/urlUtil';
import { AgentMessageModel, AgentModel, AgentThreadModel, SortDirection } from '../../../../../gamma-api';
import { defaultApi } from '../../../../apis';
import { formatDateTimeIso, formatDateTimeShort } from '../../../../utils/stringUtil';
import IbTable from '../../../../components/common/IbTable';
import IbPagination from '../../../../components/common/IbPagination';
import ResizableDrawer from '../../../../components/common/ResizableDrawer';
import AgentTestingChat from '../../../../components/AgentTestingChat';
import FilterableDebounceInput, {
  FilterableDebounceInputParam,
  IFilterableDebounceInputValue,
} from '../../../../components/common/FilterableDebounceInput';

import './index.less';

interface IThreadsTabProps {
  agent?: AgentModel;
}

const THREADS_PAGE_SIZE = 30;
const THREAD_ID_PARAM = 'threadId';

const ThreadsTab: React.FC<IThreadsTabProps> = ({ agent }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { push } = useHistory();
  const query = useQuery();
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const windowSize = useWindowSize();
  const isDesktop = windowSize.width > LAYOUT_WIDTH_BREAKPOINTS.small;

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: THREADS_PAGE_SIZE,
    total: 0,
  });

  const [threadList, setThreadList] = useState<AgentThreadModel[]>([]);
  const [threadListLoading, setThreadListLoading] = useState(false);
  const [selectedThread, setSelectedThread] = useState<AgentThreadModel>();

  const [messageList, setMessageList] = useState<AgentMessageModel[]>([]);
  const [messageListLoading, setMessageListLoading] = useState(false);

  const [drawerVisible, setDrawerVisible] = useState(false);

  const [inputValue, setInputValue] = useState<IFilterableDebounceInputValue>({ searchText: '' });

  const loadThreads = async () => {
    if (!agent) return;
    try {
      setThreadListLoading(true);
      const response = await defaultApi.searchAgentAllThreads(
        [agent.agent_id],
        inputValue.searchText,
        formatDateTimeIso(inputValue.filter?.startDate, inputValue.filter?.startTime),
        formatDateTimeIso(inputValue.filter?.endDate, inputValue.filter?.endTime),
        null,
        SortDirection.Desc,
        (pagination.current || 1) - 1,
        THREADS_PAGE_SIZE,
        true
      );
      setThreadList(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (e) {
      addAlert({ content: t('Agent: Threads: Failed to load threads'), type: 'error' });
    } finally {
      setThreadListLoading(false);
    }
  };

  const loadThread = async (threadId: string) => {
    if (!agent) return;
    try {
      const response = await defaultApi.getOneAgentThread(threadId);
      setSelectedThread(response.data);
    } catch (e) {
      addAlert({ content: t('Agent: Threads: Failed to load thread'), type: 'error' });
    }
  };

  const fetchMessages = async () => {
    if (!selectedThread) return;
    try {
      setMessageListLoading(true);
      const response = await defaultApi.searchAgentAllMessages(
        null,
        [selectedThread.thread_id],
        null,
        null,
        0,
        LARGE_PAGE_SIZE
      );
      setMessageList(response.data.items || []);
    } catch (err) {
      addAlert({ content: t('Agent: Threads: Failed to load messages'), type: 'error' });
    } finally {
      setMessageListLoading(false);
    }
  };

  useEffect(() => {
    const threadId = query.get(THREAD_ID_PARAM);
    if (!threadId) {
      setSelectedThread(undefined);
      setDrawerVisible(false);
    } else if (!selectedThread) {
      void loadThread(threadId);
      setDrawerVisible(true);
    }
  }, [location]);

  useEffect(() => {
    void fetchMessages();
  }, [selectedThread]);

  useEffect(() => {
    void loadThreads();
  }, [pagination.current, pagination.pageSize, inputValue.searchText, inputValue.filter, agent]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== pagination.current) {
      setPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [inputValue.searchText, inputValue.filter]);

  useUpdateEffect(() => {
    if (pagination.current) {
      query.set(PAGE_INDEX_PARAM, pagination.current.toString());
      push({ pathname: location.pathname, search: query.toString() });
    }
  }, [pagination.current]);

  const renderDate = (item: AgentThreadModel) => {
    return formatDateTimeShort(item.created_on, i18n.language);
  };

  const columns: ColumnType<AgentThreadModel>[] = [
    {
      title: t('Agent: Threads: Table: Name'),
      render: (_, record) => {
        return record.name;
      },
      width: isDesktop ? '50%' : undefined,
      ellipsis: true,
    },
    {
      title: t('Agent: Threads: Table: Thread start date'),
      render: renderDate,
    },
  ];

  const getThreadRowKey = (thread: AgentThreadModel) => thread.thread_id;

  const onThreadRowClick = (thread: AgentThreadModel) => {
    setSelectedThread(thread);
    query.set(THREAD_ID_PARAM, thread.thread_id);
    push({ pathname: location.pathname, search: query.toString() });
    setDrawerVisible(true);
  };

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onDrawerClose = () => {
    setSelectedThread(undefined);
    query.delete(THREAD_ID_PARAM);
    push({ pathname: location.pathname, search: query.toString() });
    setDrawerVisible(false);
  };

  const drawerTitle = () => {
    if (!selectedThread) return;
    return <span>{`${t('Agent: Threads: Message List: Title')} ${renderDate(selectedThread)}`}</span>;
  };

  return (
    <div className="threads-tab">
      <FilterableDebounceInput
        params={[FilterableDebounceInputParam.DateTime]}
        value={inputValue}
        onChange={setInputValue}
      />
      <IbTable
        columns={columns}
        dataSource={threadList}
        loading={threadListLoading}
        rowKey={getThreadRowKey}
        onRowClick={onThreadRowClick}
      />
      {!threadListLoading && (pagination.total ?? 0) > THREADS_PAGE_SIZE && (
        <div className="threads-tab__pagination-row">
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={THREADS_PAGE_SIZE}
            pageSize={THREADS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
      <ResizableDrawer
        resizable
        bodyStyle={{ padding: 0 }}
        title={drawerTitle()}
        visible={drawerVisible}
        width="75%"
        onClose={onDrawerClose}
      >
        <AgentTestingChat readonly agent={agent} loading={messageListLoading} messageList={messageList} />
      </ResizableDrawer>
    </div>
  );
};

export default ThreadsTab;
